import * as React from 'react'
import { PlatformMessageDto } from '@halo/dtos/platform-message.dto'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc'
import { MessageTableDataDto } from '@halo/dtos/platform-message-table-data-dto'
import { Button, FilterBar } from '@pushly/aqe/lib/components'
import type { ActiveFilter, ButtonAction, FilterDefinition } from '@pushly/aqe/lib/components'
import { CopyOutlined, DeleteFilled, EditOutlined, Loading3QuartersOutlined, MessageOutlined } from '@ant-design/icons'
import { Flex, Input, Modal, Table } from 'antd'
import * as moment from 'moment'
import { PlatformMessageEditor } from '@halo/components/platform-message-form/platform-message-form'
import { classnames } from '@halo/_utils/classnames'
import randomstring from 'randomstring'
import './platform-messages.scss'
import deepEqual from 'react-fast-compare'
import { ContentState, convertFromHTML, EditorState } from 'draft-js'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import EntityDisplay, { EntityTitle } from '@halo/components/data-table-view/entity-display'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { fmtDateString } from '@halo/helpers/formatting'
import { noop } from '@pushly/aqe/lib/utils/noop'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

interface IPlatformMessageList {
    /** Hide the in-table-header New Message button so a parent can render it elsewhere. */
    hideCreateButton?: boolean
}

interface IPMLState {
    loading: boolean
    allMessages: PlatformMessageDto[]
    visibleMessages: PlatformMessageDto[]
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
    tagFilter: string[]
    tagOptions: Array<{ value: string; label: string }>
    showMessageEditor: boolean
    drawerKey?: string
    messageToEdit?: any
}

export class PlatformMessageList extends React.Component<IPlatformMessageList, IPMLState> {
    protected appState: AppState
    protected appService: AppService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)

        this.state = {
            loading: true,
            allMessages: [],
            visibleMessages: [],
            page: 1,
            pageSize: 15,
            showMessageEditor: false,
            tagFilter: [],
            tagOptions: [],
        }
    }

    public componentDidMount(): void {
        this.fetchMessages()
        this.fetchFilters()
    }

    public render() {
        const filterDefinitions: FilterDefinition[] = [
            {
                key: 'tags',
                label: 'Tags',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: true,
                selectOptions: this.state.tagOptions,
            },
        ]

        const getFilterBarValue = (): ActiveFilter[] => {
            const active: ActiveFilter[] = []
            if (this.state.tagFilter.length > 0) {
                active.push({ key: 'tags', operator: 'in', value: this.state.tagFilter })
            }
            return active
        }

        const handleFilterBarApply = (filters: ActiveFilter[]) => {
            const nextTags = (filters.find((f) => f.key === 'tags')?.value as string[]) ?? []
            this.filterMessageTags(nextTags)
        }

        const tableHeader = (
            <Flex className="platform-message-list-header" gap={8} justify="space-between" align="center">
                <Flex flex={1} gap={8} align="center">
                    <Input.Search
                        className="platform-message-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Message"
                        enterButton={true}
                        onSearch={this.searchMessages}
                    />
                    <FilterBar
                        filterDefinitions={filterDefinitions}
                        value={getFilterBarValue()}
                        onApply={handleFilterBarApply}
                        showInlineTags={false}
                        showInlineClearAll={false}
                        size="middle"
                    />
                </Flex>
                {!this.props.hideCreateButton && (
                    <Flex align="center">
                        <Button type="primary" onClick={this.openDrawer}>
                            New Message
                        </Button>
                    </Flex>
                )}
            </Flex>
        )

        return (
            <div className={classnames('platform-message-list')}>
                <Table
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    // scroll.y used to freeze header row
                    // scroll.x used to fix bug with col width and scroll.y
                    // https://github.com/ant-design/ant-design/issues/7603#issuecomment-697600114
                    scroll={{ y: 555, x: 'max-content' }}
                    dataSource={this.state.visibleMessages}
                    loading={
                        this.state.loading && {
                            indicator: <Loading3QuartersOutlined spin={true} />,
                        }
                    }
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={this.state.paginationLinks}
                            onPrev={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchMessages())
                            }}
                            onNext={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchMessages())
                            }}
                        />
                    )}
                    locale={{
                        emptyText: <AntdTableEmptyPlaceholder text="No Messages" icon={<MessageOutlined />} />,
                    }}
                >
                    <Table.Column
                        key="message"
                        title="Message"
                        render={(msg: any) => {
                            const { title, message, id } = msg
                            return (
                                <EntityDisplay
                                    top={{
                                        left: <EntityTitle>{title}</EntityTitle>,
                                    }}
                                    mid={{
                                        left: <div dangerouslySetInnerHTML={{ __html: message }} />,
                                    }}
                                    bottom={{
                                        left: <span className="message-id">#{id}</span>,
                                    }}
                                />
                            )
                        }}
                        className="message"
                    />
                    <Table.Column
                        key="delivery"
                        title="Delivery"
                        render={this.renderDelivery}
                        className="delivery"
                    />
                    <Table.Column
                        key="actions"
                        title="Actions"
                        align="center"
                        className="actions"
                        render={(_, msg: any) => this.renderRowActions(msg)}
                    />
                </Table>

                <PlatformMessageEditor
                    key={this.state.drawerKey}
                    visible={this.state.showMessageEditor}
                    onClose={this.handleDrawerClose}
                    allMessages={this.state.visibleMessages}
                    message={this.state.messageToEdit && this.configMessageToEdit()}
                    onMessageUpdated={this.onMessageUpdate}
                />
            </div>
        )
    }

    protected renderRowActions(msg: any): React.ReactNode {
        const actions: ButtonAction[] = []

        if (moment(msg.end_time) > moment() || msg.end_time === null) {
            actions.push({
                key: 'edit',
                text: 'Edit',
                icon: <EditOutlined />,
                onClick: () => this.handleEditMessage(msg),
            })
        }

        actions.push({
            key: 'duplicate',
            text: 'Duplicate',
            icon: <CopyOutlined />,
            onClick: () => this.handleDuplicateMsg(msg),
        })

        return (
            <Button
                size="small"
                icon={<DeleteFilled />}
                onClick={() => this.deleteMessage(msg)}
                actions={actions}
            >
                Delete
            </Button>
        )
    }

    protected searchMessages = async (data: string | any): Promise<void> => {
        const value = typeof data !== 'string' ? data.target.value : data
        let query = (value ?? '').trim()

        if (query.length < 2) {
            query = undefined
        }

        await this.setState({ query, page: 1 })
        return this.fetchMessages()
    }

    protected filterMessageTags = async (tagFilter: string[]) => {
        if (!deepEqual(tagFilter, this.state.tagFilter)) {
            await this.setState({ tagFilter: tagFilter ?? [], page: 1 })
            return this.fetchMessages()
        }
    }

    protected onMessageUpdate = () => {
        this.handleDrawerClose()
        this.fetchMessages()
    }

    public openDrawer = async (): Promise<void> => {
        return this.setState({ showMessageEditor: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showMessageEditor: false, messageToEdit: undefined })
    }

    protected deleteMessage = async (msg: any) => {
        Modal.confirm({
            okText: 'Delete',
            okButtonProps: {
                size: 'small',
            },
            cancelButtonProps: {
                size: 'small',
            },
            title: 'Delete Message',
            content: (
                <>
                    <p>Are you sure you want to (soft) delete the following message?</p>
                    <p>
                        <b>Title:</b> {msg.title}
                        <br />
                        <b>ID:</b> {msg.id}
                        <br />
                    </p>
                </>
            ),
            onOk: async (..._args: any[]) => {
                const res = await this.appService.destroyMessage(msg)

                if (res) {
                    simpleNotification('success', 'Schedule successfully deleted.')

                    this.fetchMessages()
                } else {
                    console.warn(res)
                    this.fetchMessages()

                    return simpleNotification(
                        'error',
                        'The message is unable to be updated at this time. See console for more details,',
                    )
                }
            },
            onCancel: noop,
        })
    }

    protected handleEditMessage = async (msg: any) => {
        this.setState({
            drawerKey: randomstring.generate(),
            messageToEdit: msg,
            showMessageEditor: true,
        })
    }

    protected handleDuplicateMsg = async (msg: any) => {
        const duplicateMessage: any = {
            title: msg.title,
            message: msg.message,
            start_time: msg.start_time,
            end_time: msg.end_time,
        }

        if (msg.domainIds) {
            duplicateMessage.domain_ids = msg.domain_ids
        }
        if (msg.minVersionReq) {
            duplicateMessage.min_version_req = msg.min_version_req
        }
        if (msg.maintenanceMode) {
            duplicateMessage.maintenance_mode = msg.maintenance_mode
        }

        this.setState({
            drawerKey: randomstring.generate(),
            messageToEdit: duplicateMessage,
            showMessageEditor: true,
        })
    }

    protected renderMessage(text: string, msg: MessageTableDataDto): React.ReactNode {
        return (
            <EntityDisplay
                top={{
                    left: <EntityTitle>{msg.title}</EntityTitle>,
                }}
                mid={{
                    left: <div dangerouslySetInnerHTML={{ __html: msg.message }} />,
                }}
            />
        )
    }

    protected renderDelivery(text: string, msg: any): React.ReactNode {
        return !msg.start_time ? (
            <>--</>
        ) : (
            <EntityDisplay
                top={{ left: <span>{msg.start_time ? fmtDateString(msg.start_time, 'M/D LT') : 'Now'} -</span> }}
                mid={{ left: <span>{msg.end_time ? fmtDateString(msg.end_time, 'M/D LT') : 'On-Going'}</span> }}
            />
        )
    }

    protected configMessageToEdit() {
        const { messageToEdit } = this.state
        let message: any = {}
        message = Object.assign(message, messageToEdit)

        if (message.message) {
            const content = convertFromHTML(message.message)
            message.message = EditorState.createWithContent(
                ContentState.createFromBlockArray(content.contentBlocks, content.entityMap),
            )
        }

        return message
    }

    protected fetchFilters = async (): Promise<void> => {
        try {
            const filtersOpts = await this.appService.getFilterOpts()

            if (!!filtersOpts) {
                const tagOptions = filtersOpts.map((tag: string) => ({
                    value: tag,
                    label: titleCase(tag),
                }))

                this.setState({ tagOptions })
            }
        } catch (error) {
            simpleNotification('error', error)
        }
    }

    protected fetchMessages = async (): Promise<void> => {
        await this.setState({ loading: true })
        const options = {
            showLoadingScreen: false,
            query: {
                search: this.state.query,
                tags: this.state.tagFilter.length > 0 ? this.state.tagFilter : undefined,
            },
        }

        try {
            const { ok, data, meta } = await this.appService.fetchPlatformMessages(options)
            if (ok) {
                const messageDtos = data.map((msg: PlatformMessageDto) => {
                    return MessageTableDataDto.fromMessageDto(msg)
                })

                await this.setState((currState) => ({
                    loading: false,
                    allMessages: messageDtos,
                    visibleMessages: messageDtos,
                    paginationLinks: meta.links ?? currState.paginationLinks,
                }))
            }
        } catch (error) {
            simpleNotification('error', error)
        }

        this.appService.unsetModuleLoading()
    }
}
